import { state } from '@/stores/deluge.js';
import { deluge } from '@/stores/deluge.js';
import lightNDI from '../assets/NDI Logo Master - Light.png';

export function assetSmallThumbnail(asset, static_or_motion = 'static') {
  // check if object or string
  let prevExt = static_or_motion === 'static' ? '.jpg' : '.mp4';
  if (typeof asset === 'string') {
    // check if it's not a uuid
    if (asset.length !== 36) {
      return null;
    }
    return (
      state.devRedirect +
      state.$ident +
      `/thumbnails/previews/${static_or_motion}/small/` +
      asset +
      prevExt
    );
  } else if (asset) {
    // Aggregated assets from the cloud's content_snapshot stream can
    // arrive with asset_type missing — be defensive.
    const assetType = (asset.asset_type || '').toLowerCase();
    if (assetType === 'ndi') {
      return lightNDI;
    }
    // video_input assets (hardware capture devices) don't have thumbnails
    if (assetType === 'video_input') {
      return null;
    }
    if (
      (!assetType.includes('video') && static_or_motion === 'motion') ||
      (!assetType.includes('image') && !assetType.includes('video'))
    ) {
      return null;
    }
    // No thumbnail file exists for this asset (preview_* flags are the
    // backend's contract — falsy means "don't try to fetch"). Returning
    // null skips the <img> render and avoids the 404. Especially relevant
    // in cloud mode where player-owned assets don't have their thumbs
    // mirrored to R2.
    const flag = static_or_motion === 'static'
      ? asset.preview_static_small
      : asset.preview_motion_small;
    if (!flag) {
      return null;
    }
    return (
      state.devRedirect +
      state.$ident +
      `/thumbnails/previews/${static_or_motion}/small/` +
      asset.uid +
      prevExt
    );
  }
  return null;
}
export function assetWideThumbnail(asset) {
  if (typeof asset === 'string') {
    return (
      state.devRedirect + '/thumbnails/previews/static/wide/' + asset + '.jpg'
    );
  }
  if (!asset) return null;
  if (asset.asset_type === 'ndi') {
    return lightNDI;
  }
  // Mirror assetSmallThumbnail: skip the URL when no wide thumb exists.
  // Fall back to the small thumbnail when only that one is available, so
  // SceneCard shows *something* instead of a 404 broken-image.
  if (asset.preview_static_wide) {
    return (
      state.devRedirect +
      '/thumbnails/previews/static/wide/' +
      asset.uid +
      '.jpg'
    );
  }
  if (asset.preview_static_small) {
    return assetSmallThumbnail(asset);
  }
  return null;
}

// Convert any CSS color string to rgb(r,g,b) format for shadeColor compatibility
function toRgb(color) {
  if (!color || typeof color !== 'string') return null;
  color = color.trim();
  // Already rgb(...) or rgba(...)
  if (color.startsWith('rgb')) return color.replace(/rgba?\(([^)]+)\)/, (_, inner) => {
    const parts = inner.split(',').map(s => s.trim());
    return `rgb(${parts[0]},${parts[1]},${parts[2]})`;
  });
  // Hex color
  if (color.startsWith('#')) {
    let hex = color.slice(1);
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    if (hex.length === 8) hex = hex.slice(0, 6); // strip alpha
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if (!isNaN(r) && !isNaN(g) && !isNaN(b)) return `rgb(${r},${g},${b})`;
  }
  return null;
}

// New helper: resolve thumbnail and color for a Scene (or item that references a template)
// Returns an object: { src: string|null, color: string|undefined }
export function getThumbnailForScene(scene, stores = null) {
  const store = stores || deluge || {};
  if (!scene) return { src: null, color: undefined };

  // If caller passed an index-like object (from store.items), normalize to actual scene
  let resolvedScene = scene;
  try {
    if (scene.did && store.items?.[scene.did]) {
      resolvedScene = store.items[scene.did];
    }
  } catch (e) {
    // ignore
  }

  // 1) Check scene svars for an asset thumbnail
  try {
    if (resolvedScene.svars && typeof resolvedScene.svars === 'object') {
      for (const svar in resolvedScene.svars) {
        if (resolvedScene.svars[svar]?.type === 'asset') {
          const assetUid = resolvedScene.svars[svar].value;
          if (store?.assets && store.assets[assetUid]) {
            const asset = store.assets[assetUid];
            const src = asset.preview_static_wide
              ? assetWideThumbnail(asset)
              : assetSmallThumbnail(asset);
            return { src, color: asset.info?.color };
          }
          // Asset not in store (e.g. assets loaded via HTTP pagination),
          // fall back to constructing thumbnail URL from UID directly
          if (assetUid) {
            return { src: assetSmallThumbnail(assetUid), color: undefined };
          }
        }
      }
    }
  } catch (e) {
    // If svars is malformed, ignore and continue to template fallback
  }

  // 2) If scene references a template, inspect template.scene_data for an asset
  const template = store?.templates?.[resolvedScene.scene_name];
  if (template) {
    try {
      if (template.scene_data && typeof template.scene_data === 'object') {
        for (const svar of Object.keys(template.scene_data)) {
          if (template.scene_data[svar]?.type === 'asset') {
            const assetUid = template.scene_data[svar].value;
            if (store?.assets && store.assets[assetUid]) {
              const asset = store.assets[assetUid];
              const src = asset.preview_static_wide
                ? assetWideThumbnail(asset)
                : assetSmallThumbnail(asset);
              return { src, color: asset.info?.color };
            }
            // Asset not in store, fall back to thumbnail URL from UID
            if (assetUid) {
              return { src: assetSmallThumbnail(assetUid), color: undefined };
            }
          }
        }
      }
    } catch (e) {
      // ignore malformed scene_data
    }

    // 3) Fall back to template thumbnail
    if (template.thumbnail) {
      return { src: template.thumbnail, color: template.color };
    }
  }

  // 4) No asset found — check for a color variable in svars, then template scene_data
  try {
    if (resolvedScene.svars && typeof resolvedScene.svars === 'object') {
      for (const svar in resolvedScene.svars) {
        if (resolvedScene.svars[svar]?.type === 'color' && resolvedScene.svars[svar]?.value) {
          const rgb = toRgb(resolvedScene.svars[svar].value);
          if (rgb) return { src: null, color: rgb };
        }
      }
    }
  } catch (e) {
    // ignore
  }
  if (template?.scene_data && typeof template.scene_data === 'object') {
    try {
      for (const svar of Object.keys(template.scene_data)) {
        if (template.scene_data[svar]?.type === 'color' && template.scene_data[svar]?.value) {
          const rgb = toRgb(template.scene_data[svar].value);
          if (rgb) return { src: null, color: rgb };
        }
      }
    } catch (e) {
      // ignore
    }
  }

  // 5) Nothing found
  return { src: null, color: undefined };
}

// Generic thumbnail resolver for assets, scenes, or templates.
// Returns a string src or null.
export function getThumbnail(item, options = {}) {
  const store = options.stores || deluge || {};
  if (!item) return null;

  // Asset uid or asset object
  if (typeof item === 'string') {
    if (item.length === 36) {
      return assetSmallThumbnail(item);
    }
    // otherwise assume it's already a URL
    return item;
  }

  // Asset object
  if (item.uid && item.asset_type) {
    return item.preview_static_wide
      ? assetWideThumbnail(item)
      : assetSmallThumbnail(item);
  }

  // Scene-like object (has svars or scene_name)
  if (item.svars || item.scene_name || item.did) {
    try {
      const r = getThumbnailForScene(item, store);
      return r?.src || null;
    } catch (e) {
      return null;
    }
  }

  // Template-like object (has scene_data or thumbnail)
  if (item.scene_data) {
    for (const svar of Object.keys(item.scene_data)) {
      if (item.scene_data[svar].type === 'asset') {
        const assetUid = item.scene_data[svar].value;
        if (store.assets?.[assetUid]) {
          const asset = store.assets[assetUid];
          return asset.preview_static_wide
            ? assetWideThumbnail(asset)
            : assetSmallThumbnail(asset);
        }
        // Asset not in store, fall back to thumbnail URL from UID
        if (assetUid) {
          return assetSmallThumbnail(assetUid);
        }
      }
    }
    if (item.thumbnail) return item.thumbnail;
  }

  // Fallback to thumbnail property if present
  if (item.thumbnail) return item.thumbnail;

  return null;
}
